import { useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { BodyMedium, ParagraphSmallText, TitleSmall } from '../Texts';
import { formatUSDEnsure2Digits } from '../../common/currency';
import { hapticsImpactLight } from '../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { usePrivy } from '@privy-io/react-auth';
import { StaggerContainer, StaggerItem } from '../Stagger';

const TokenContainer = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  padding: 12px 10px;
  gap: 8px;
  display: flex;
  align-items: center;
  height: 60px;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: opacity 0.2s;
  touch-action: manipulation;

  &:hover {
    opacity: 0.7;
  }
`;

const TokenScroll = styled.div`
  ${({ $isMobile }) => ($isMobile ? 'height: calc(100vh - 270px);' : '')}
  width: 100%;
  overflow-y: scroll;
  max-height: ${({ $isMobile }) => ($isMobile ? 'unset' : '400px')};
  -webkit-overflow-scrolling: touch;
`;

const MAX_STAGGER = 10;

const TokenList = ({
  tokens,
  isLoading,
  isFetching,
  hasMore,
  loadMore,
  onTokenSelect,
  selectedTokenAddress = null,
  selectedNetwork = null,
  suggestedToken = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { ref, inView } = useInView({ threshold: 0.1 });

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isFetching) {
      loadMore();
    }
  }, [inView, hasMore, isFetching, loadMore]);

  const { user } = usePrivy();

  const showBalances = !!user;
  const showSuggested = !user;

  return (
    <TokenScroll $isMobile={isMobile}>
      <StaggerContainer>
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {isLoading && tokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading tokens...
            </div>
          ) : tokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              No tokens found matching your search.
            </div>
          ) : (
            tokens.map((token, index) => {
              const isLast = index === tokens.length - 1;
              const isSelected =
                selectedTokenAddress === token.address &&
                selectedNetwork === token.network;

              const delay = index < MAX_STAGGER ? index : 0;
              const shouldAnimate = index < MAX_STAGGER;

              const tokenContent = (
                <>
                  <img
                    width={40}
                    height={40}
                    src={token.logoURI}
                    alt={token.symbol}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                    }}
                  >
                    <TitleSmall style={{ color: theme.text.title }}>
                      {token.name?.length > 15
                        ? `${token.name.slice(0, 15)}...`
                        : token.name}
                    </TitleSmall>
                    {showBalances && (
                      <ParagraphSmallText
                        style={{ color: theme.text.secondary, fontSize: 12 }}
                      >
                        {`${token?.balance ? token?.balance : 0} ${token?.symbol}`}
                      </ParagraphSmallText>
                    )}
                  </div>
                  {showBalances && (
                    <BodyMedium
                      style={{ marginLeft: 'auto', color: theme.text.title }}
                    >
                      {formatUSDEnsure2Digits(
                        token.usdValue ? token.usdValue : 0,
                      )}
                    </BodyMedium>
                  )}

                  {showSuggested && suggestedToken && (
                    <BodyMedium
                      style={{
                        marginLeft: 'auto',
                        color: theme.background.primary,
                        fontWeight: 600,
                        padding: '4px 8px',
                        borderRadius: '999px',
                        backgroundColor: theme.status.error.primary,
                      }}
                    >
                      Free Gift
                    </BodyMedium>
                  )}
                </>
              );

              return shouldAnimate ? (
                <StaggerItem
                  key={`${token.address}-${token.network || 'default'}-${index}`}
                  $delay={delay}
                >
                  <TokenContainer
                    ref={isLast ? ref : null}
                    onClick={() => {
                      hapticsImpactLight();
                      onTokenSelect(token);
                    }}
                    style={{
                      border: isSelected
                        ? `2px solid ${theme.primary.primary}`
                        : '1px solid transparent',
                      backgroundColor: isSelected
                        ? theme.background.secondary
                        : theme.background.light,
                    }}
                  >
                    {tokenContent}
                  </TokenContainer>
                </StaggerItem>
              ) : (
                <TokenContainer
                  key={`${token.address}-${token.network || 'default'}-${index}`}
                  ref={isLast ? ref : null}
                  onClick={() => {
                    hapticsImpactLight();
                    onTokenSelect(token);
                  }}
                  style={{
                    border: isSelected
                      ? `2px solid ${theme.primary.primary}`
                      : '1px solid transparent',
                    backgroundColor: isSelected
                      ? theme.background.secondary
                      : theme.background.light,
                  }}
                >
                  {tokenContent}
                </TokenContainer>
              );
            })
          )}
          {isFetching && tokens.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading more tokens...
            </div>
          )}
        </div>
      </StaggerContainer>
    </TokenScroll>
  );
};

export default TokenList;
